import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Observable, Subject, takeUntil, tap } from 'rxjs';


import {
  GetNutrientAdviceBemResponse,
  NutrientAdviceCalculationBemResponse,
  NutrientAdviceCalculationDto,
  NutrientGroupDto,
} from 'src/app/bem/shared/models/models';

import { BemAIIdentifiers, eLearningUrl } from '../../../shared/constants';
import { BemUtils } from '../../shared/bem-utils';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { NutrientAdviceTabComponent } from '../nutrient-advice-tab/nutrient-advice-tab.component';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe, DatePipe } from '@angular/common';

@Component({
    selector: 'app-nutrient-advice',
    templateUrl: './nutrient-advice.component.html',
    styleUrls: ['./nutrient-advice.component.scss'],
    imports: [
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatIconButton,
        MatIcon,
        MatTabGroup,
        MatTab,
        NutrientAdviceTabComponent,
        MatCardContent,
        NotificationEmptyComponent,
        MatProgressSpinner,
        AsyncPipe,
        DatePipe,
        TranslatePipe,
    ],
})
export class NutrientAdviceComponent implements OnInit, OnDestroy {
  private readonly localeService = inject(LocaleService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  @Input() nutrientAdviceData$!: Observable<GetNutrientAdviceBemResponse | null>;

  selectedTabIndex = 0;
  newestTabIndex = 0;
  locale!: string;

  private readonly destroy$ = new Subject<void>();

  public constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
  }

  ngOnInit(): void {
    if (this.nutrientAdviceData$) {
      this.nutrientAdviceData$.pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          if (this.isSuccessResponse(response)) {
            const newestSampleMomentIndex = this.getNewestSampleMomentNumber(response.nutrientAdviceCalculation) - 1;
            this.selectedTabIndex = newestSampleMomentIndex;
            this.newestTabIndex = newestSampleMomentIndex;
          }
        }),
      ).subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isSuccessResponse(response: GetNutrientAdviceBemResponse | null): response is NutrientAdviceCalculationBemResponse {
    return !!response && 'cropField' in response && 'nutrientAdviceCalculation' in response;
  }

  selectTab(index: number): void {
    this.ai.trackEvent(BemAIIdentifiers.changeTab, {});
    this.selectedTabIndex = index;
  }

  redirectToELearning(): void {
    this.ai.trackEvent(BemAIIdentifiers.clickInfoInsightsPage, {});
    window.open(eLearningUrl);
  }

  private getNewestSampleMomentNumber(nutrientAdviceCalculation: NutrientAdviceCalculationDto | undefined): number {
    let highestSampleMoment = 1;
    if (!nutrientAdviceCalculation) {
      return highestSampleMoment;
    }
    const sampleKeys = Object.keys(nutrientAdviceCalculation) as Array<keyof NutrientAdviceCalculationDto>;

    for (const sampleKey of sampleKeys) {
      const splittedKey = BemUtils.seperateNutrientAndTextId(sampleKey);
      if (!splittedKey) {
        continue;
      }
      const nutrientGroup = nutrientAdviceCalculation[sampleKey] as NutrientGroupDto;
      if (BemUtils.hasValidData(nutrientGroup) && +splittedKey.textId > highestSampleMoment) {
        highestSampleMoment = +splittedKey.textId;
      }
    }
    return highestSampleMoment;
  }
}
